import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { LoadingState, ErrorState, EmptyState } from "@/components/ui/AsyncState";
import { LeadStatusBadge, QualificationBadge, LEAD_STATUS_OPTIONS } from "@/components/ui/StatusBadge";
import { useApi } from "@/lib/use-api";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { formatCurrencyFromCents, formatPhone, formatRelativeTime } from "@/lib/format";
import type { Lead, LeadStatus } from "@platform/shared";

const SOURCE_LABEL: Record<Lead["source"], string> = {
  missed_call: "Missed call",
  manual: "Manual",
  other: "Other",
};

interface NewLeadFormState {
  name: string;
  phone: string;
  email: string;
  service: string;
}

const EMPTY_FORM: NewLeadFormState = { name: "", phone: "", email: "", service: "" };

function NewLeadForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewLeadFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiFetch("/api/leads", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim() || undefined,
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          service: form.service.trim() || undefined,
          source: "manual",
        }),
      });
      setForm(EMPTY_FORM);
      setOpen(false);
      onCreated();
    } catch (err) {
      setFormError(err instanceof ApiRequestError ? err.message : "Couldn't create lead.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <Button variant="primary" onClick={() => setOpen(true)}>
        + New lead
      </Button>
    );
  }

  return (
    <Card className="p-5">
      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <p className="font-medium text-ink">New lead</p>
          <p className="mt-0.5 text-xs text-ink-soft">Log a lead manually (e.g. one taken over the phone).</p>
        </div>
        <label className="text-sm text-ink-soft">
          Phone <span className="text-signal-600">*</span>
          <input
            required
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+1 555 000 1234"
            className="mt-1 w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink"
          />
        </label>
        <label className="text-sm text-ink-soft">
          Name
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="mt-1 w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink"
          />
        </label>
        <label className="text-sm text-ink-soft">
          Email
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-1 w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink"
          />
        </label>
        <label className="text-sm text-ink-soft">
          Service
          <input
            value={form.service}
            onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
            className="mt-1 w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink"
          />
        </label>
        {formError && <p className="text-sm text-signal-600 sm:col-span-2">{formError}</p>}
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Create lead"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setOpen(false);
              setFormError(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function Leads() {
  const { data, isLoading, error, refetch } = useApi<{ leads: Lead[] }>("/api/leads");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all" | "needs_review">("all");

  const leads = data?.leads ?? [];
  const filtered = useMemo(
    () =>
      statusFilter === "all"
        ? leads
        : statusFilter === "needs_review"
          ? leads.filter((l) => l.needsHuman)
          : leads.filter((l) => l.status === statusFilter),
    [leads, statusFilter]
  );
  const needsReviewCount = leads.filter((l) => l.needsHuman).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-ink">Leads</h1>
          <p className="mt-1 text-sm text-ink-soft">Every customer who reached out after a missed call.</p>
        </div>
        <NewLeadForm onCreated={refetch} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setStatusFilter("all")}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            statusFilter === "all"
              ? "border-brand-700 bg-brand-700 text-white"
              : "border-line text-ink-soft hover:text-ink"
          }`}
        >
          All ({leads.length})
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter("needs_review")}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            statusFilter === "needs_review"
              ? "border-signal-600 bg-signal-600 text-white"
              : "border-signal-300 text-signal-600 hover:bg-signal-50"
          }`}
        >
          Needs review ({needsReviewCount})
        </button>
        {LEAD_STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setStatusFilter(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              statusFilter === opt.value
                ? "border-brand-700 bg-brand-700 text-white"
                : "border-line text-ink-soft hover:text-ink"
            }`}
          >
            {opt.label} ({leads.filter((l) => l.status === opt.value).length})
          </button>
        ))}
      </div>

      {isLoading && <LoadingState label="Loading leads…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState>
          {leads.length === 0
            ? "No leads yet. They'll show up here once Callback starts responding to missed calls."
            : "No leads match this filter."}
        </EmptyState>
      )}

      {filtered.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-paper text-ink-soft">
              <tr>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Phone</th>
                <th className="px-5 py-2.5 font-medium">Service</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 font-medium">Qualification</th>
                <th className="px-5 py-2.5 font-medium">Source</th>
                <th className="px-5 py-2.5 font-medium">Est. value</th>
                <th className="px-5 py-2.5 font-medium">Created</th>
                <th className="px-5 py-2.5 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead) => (
                <tr key={lead.id} className="border-b border-line last:border-0 hover:bg-paper">
                  <td className="px-5 py-3 font-medium text-ink">{lead.name ?? "Unknown"}</td>
                  <td className="px-5 py-3 text-ink-soft">{formatPhone(lead.phone)}</td>
                  <td className="px-5 py-3 text-ink-soft">{lead.service ?? "—"}</td>
                  <td className="px-5 py-3">
                    <LeadStatusBadge status={lead.status} />
                  </td>
                  <td className="px-5 py-3">
                    <QualificationBadge decision={lead.qualificationStatus} />
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{SOURCE_LABEL[lead.source]}</td>
                  <td className="px-5 py-3 text-ink-soft">{formatCurrencyFromCents(lead.estimatedValueCents)}</td>
                  <td className="px-5 py-3 text-ink-soft">{formatRelativeTime(lead.createdAt)}</td>
                  <td className="px-5 py-3">
                    <Link to={`/dashboard/leads/${lead.id}`} className="font-medium text-brand-700 hover:underline">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
